import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useColorMode } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaUser, FaEnvelope, FaPhone, FaClipboardList, 
  FaCalendarAlt, FaClock, FaPaperPlane, FaArrowRight, FaArrowLeft, FaShieldAlt
} from "react-icons/fa";

const validateEmail = (email) => {
  return String(email).toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
};

const validatePhone = (phone) => {
  const cleaned = phone.replace(/[\s.\-()+]/g, '');
  return /^(33|0)[0-9]{9}$/.test(cleaned);
};

const validateName = (name) => name.trim().length >= 2 && name.trim().length <= 60;

const Devis = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});

  // RÉCUPÉRATION DE L'URL DYNAMIQUE
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [formData, setFormData] = useState({
    nom: "", prenom: "", email: "", telephone: "",
    message: "", service: "", handicap: false,
    maladie: false, maladieRare: false, dependant: false,
    date: "", time: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const nextStep = () => {
    if (step === 1) {
      const newErrors = {};
      if (!validateName(formData.nom)) newErrors.nom = "Minimum 2 caractères, max 60";
      if (!validateName(formData.prenom)) newErrors.prenom = "Minimum 2 caractères, max 60";
      if (!validateEmail(formData.email)) newErrors.email = "Adresse email invalide";
      if (!validatePhone(formData.telephone)) newErrors.telephone = "Format invalide (ex: 06 12 34 56 78)";
      setErrors(newErrors);
      if (Object.keys(newErrors).length === 0) setStep((s) => s + 1);
    } else {
      setStep((s) => s + 1);
    }
  };
  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (formData.message.trim().length < 10) {
      setErrors((prev) => ({ ...prev, message: "Message trop court (minimum 10 caractères)" }));
      return;
    }
    setLoading(true);

    const situationArray = [];
    if (formData.handicap) situationArray.push("Handicap");
    if (formData.maladie) situationArray.push("Maladie");
    if (formData.maladieRare) situationArray.push("Maladie Rare");
    if (formData.dependant) situationArray.push("Dépendant");

    const payload = {
      ...formData,
      situation: situationArray.join(", ") || "Aucune situation spécifique",
      type: "DEVIS"
    };

    try {
      const response = await axios.post(`${API_URL}/api/contact`, payload);
      
      toast.success(response.data.message || "Demande de devis envoyée !");
      setFormData({
        nom: "", prenom: "", email: "", telephone: "", message: "",
        service: "", handicap: false, maladie: false, maladieRare: false,
        dependant: false, date: "", time: "",
      });
      setStep(1);
    } catch (error) {
      console.error("Erreur envoi devis :", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Styles réutilisables adaptés à la DA Premium
  const inputStyle = `w-full border-2 rounded-2xl p-4 transition-all outline-none text-base font-medium ${
    isDark 
      ? "bg-gray-800/50 border-gray-700 text-white focus:border-blue-500 focus:bg-gray-800 placeholder-gray-500" 
      : "bg-white border-gray-100 text-[#1B365D] focus:border-blue-600 focus:shadow-md placeholder-gray-400"
  }`;

  const labelStyle = `flex items-center text-xs font-bold uppercase tracking-widest mb-3 ${
    isDark ? "text-gray-400" : "text-blue-900/70"
  }`;

  const stepVariants = {
    initial: { x: 40, opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { x: -40, opacity: 0, transition: { duration: 0.3 } }
  };

  return (
    <>
      <Helmet>
        <title>Demande de devis gratuit — Hetep Iaout Services</title>
        <meta name="description" content="Demandez votre devis gratuit pour un service d'aide à domicile personnalisé en Île-de-France. HIS vous répond sous 24h et s'adapte à votre budget." />
        <link rel="canonical" href="https://www.hisvie.com/demande-de-devis/" />
      </Helmet>

      <div className={`relative min-h-screen pt-32 pb-24 overflow-hidden transition-colors duration-500 ${isDark ? "bg-[#0a0f16]" : "bg-[#FAFCFF]"}`}>
        
        {/* Glow abstrait en arrière-plan */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none -z-10"></div>

        {/* --- HEADER --- */}
        <header className="text-center mb-16 px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            
            <h1 className={`text-4xl md:text-6xl font-black leading-tight tracking-tight ${isDark ? "text-white" : "text-[#1B365D]"}`}>
              Votre devis <br className="hidden sm:block"/>
              <span className="text-blue-600 font-serif italic font-light">sur-mesure.</span>
            </h1>
            <p className={`mt-6 text-base md:text-lg max-w-2xl mx-auto font-light ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Renseignez vos besoins en quelques clics. Nos conseillers reviendront vers vous sous 48h avec une proposition claire et détaillée.
            </p>
          </motion.div>
        </header>

        <div className="max-w-3xl mx-auto px-4 relative z-10">
          
          {/* --- BARRE DE PROGRESSION PREMIUM --- */}
          <div className="mb-12">
            <div className={`h-2 w-full rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
              <motion.div 
                className="h-full bg-blue-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(step / 3) * 100}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            <div className={`flex justify-between mt-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <span className={step >= 1 ? "text-blue-600" : ""}>01. Coordonnées</span>
              <span className={step >= 2 ? "text-blue-600" : ""}>02. Prestation</span>
              <span className={step >= 3 ? "text-blue-600" : ""}>03. Validation</span>
            </div>
          </div>

          {/* --- FORMULAIRE --- */}
          <motion.div layout className={`shadow-2xl rounded-[2.5rem] p-8 md:p-12 border ${isDark ? "bg-[#0d131f]/90 border-gray-800 backdrop-blur-xl" : "bg-white border-white"}`}>
            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                
                {/* ÉTAPE 1 : COORDONNÉES */}
                {step === 1 && (
                  <motion.div key="step1" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-8">
                    <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Vos informations</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className={labelStyle}><FaUser className="mr-2" /> Nom</label>
                        <input type="text" name="nom" value={formData.nom} onChange={handleChange} maxLength={60} className={`${inputStyle} ${errors.nom ? 'border-red-400' : ''}`} placeholder="Votre nom" />
                        {errors.nom && <p className="text-red-500 text-xs px-1">{errors.nom}</p>}
                      </div>
                      <div className="space-y-2">
                        <label className={labelStyle}><FaUser className="mr-2" /> Prénom</label>
                        <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} maxLength={60} className={`${inputStyle} ${errors.prenom ? 'border-red-400' : ''}`} placeholder="Votre prénom" />
                        {errors.prenom && <p className="text-red-500 text-xs px-1">{errors.prenom}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className={labelStyle}><FaEnvelope className="mr-2" /> Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} maxLength={100} className={`${inputStyle} ${errors.email ? 'border-red-400' : ''}`} placeholder="exemple@mail.com" />
                        {errors.email && <p className="text-red-500 text-xs px-1">{errors.email}</p>}
                      </div>
                      <div className="space-y-2">
                        <label className={labelStyle}><FaPhone className="mr-2" /> Téléphone</label>
                        <input type="tel" name="telephone" value={formData.telephone} onChange={handleChange} maxLength={20} className={`${inputStyle} ${errors.telephone ? 'border-red-400' : ''}`} placeholder="06 12 34 56 78" />
                        {errors.telephone && <p className="text-red-500 text-xs px-1">{errors.telephone}</p>}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={nextStep}
                      className="w-full flex items-center justify-center gap-3 p-5 mt-4 bg-blue-600 text-white rounded-2xl font-bold uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/30"
                    >
                      Continuer <FaArrowRight />
                    </button>
                  </motion.div>
                )}

                {/* ÉTAPE 2 : DÉTAILS DU SERVICE */}
                {step === 2 && (
                  <motion.div key="step2" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-8">
                    <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Le service souhaité</h3>
                    
                    <div className="space-y-2">
                      <label className={labelStyle}><FaClipboardList className="mr-2" /> Sélectionnez une prestation</label>
                      <select name="service" value={formData.service} onChange={handleChange} required className={`${inputStyle} cursor-pointer`}>
                        <option value="" disabled hidden>Choisir dans la liste...</option>
                        <option value="Aide aux actes essentiels">Aide aux actes essentiels</option>
                        <option value="Gardes d'enfants">Gardes d'enfants</option>
                        <option value="Ménage de votre logement">Ménage de votre logement</option>
                        <option value="Accompagnement handicap">Accompagnement handicap</option>
                      </select>
                    </div>

                    <div className="space-y-4 pt-4">
                      <label className={labelStyle}>Situation spécifique (Optionnel)</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { n: "maladie", l: "Maladie" }, 
                          { n: "handicap", l: "Handicap" }, 
                          { n: "maladieRare", l: "Maladie Rare" }, 
                          { n: "dependant", l: "Dépendant" }
                        ].map(({ n, l }) => (
                          <label key={n} className={`flex items-center justify-center text-center px-4 py-4 rounded-2xl border-2 cursor-pointer transition-all font-bold text-[10px] md:text-xs uppercase tracking-widest select-none ${
                            formData[n] 
                              ? "bg-blue-50 border-blue-600 text-blue-700 dark:bg-blue-900/40 dark:border-blue-500 dark:text-blue-300" 
                              : isDark ? "bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-500" : "bg-gray-50 border-gray-100 text-gray-500 hover:border-blue-200"
                          }`}>
                            <input type="checkbox" name={n} checked={formData[n]} onChange={handleChange} className="hidden" />
                            {l}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <button type="button" onClick={prevStep} className={`flex-1 p-5 rounded-2xl font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 text-xs border-2 ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        <FaArrowLeft /> Retour
                      </button>
                      <button type="button" onClick={nextStep} disabled={!formData.service} className="flex-[2] p-5 bg-blue-600 text-white rounded-2xl font-bold uppercase tracking-widest hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:shadow-none">
                        Continuer <FaArrowRight />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* ÉTAPE 3 : DATE & PRÉCISIONS */}
                {step === 3 && (
                  <motion.div key="step3" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-8">
                    <h3 className={`text-2xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Planification</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className={labelStyle}><FaCalendarAlt className="mr-2" /> Date de début souhaitée</label>
                        <input type="date" name="date" value={formData.date} onChange={handleChange} required className={inputStyle} />
                      </div>
                      <div className="space-y-2">
                        <label className={labelStyle}><FaClock className="mr-2" /> Créneau préféré</label>
                        <select name="time" value={formData.time} onChange={handleChange} required className={`${inputStyle} cursor-pointer`}>
                          <option value="" disabled hidden>Sélectionner...</option>
                          {["10h - 12h", "12h - 14h", "14h - 16h", "16h - 18h", "18h - 20h"].map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    </div>
                    
                    <div className="space-y-2 pt-2">
                      <label className={labelStyle}>Précisions sur vos besoins</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} rows="4" maxLength={2000} className={`${inputStyle} resize-none ${errors.message ? 'border-red-400' : ''}`} placeholder="Détaillez ici les besoins spécifiques ou toute information utile pour le devis..."></textarea>
                      <div className="flex justify-between px-1">
                        {errors.message ? <p className="text-red-500 text-xs">{errors.message}</p> : <span />}
                        <p className={`text-xs ${formData.message.length > 1800 ? 'text-orange-500' : 'text-gray-400'}`}>{formData.message.length}/2000</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <button type="button" onClick={prevStep} className={`flex-1 p-5 rounded-2xl font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 text-xs border-2 ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        <FaArrowLeft /> Retour
                      </button>
                      <button type="submit" disabled={loading || !formData.date || !formData.time} className={`flex-[2] flex justify-center items-center p-5 font-black uppercase tracking-widest rounded-2xl shadow-xl transition-all ${loading ? "bg-gray-400 cursor-not-allowed shadow-none text-white" : "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-600/30"}`}>
                        {loading ? "Envoi en cours..." : <><FaPaperPlane className="mr-3" /> Demander mon devis</>}
                      </button>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Devis;